import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError, enabledProviders } from '../lib/supabase'
import { renderGoogleButton, waitForCredential } from '../lib/googleIdentity'
import { useTheme } from '../lib/theme'
import { usePlatform } from '../lib/platform'
import { isPasswordBreached } from '../lib/leakedPassword'
import { Shell } from '../components/Shell'
import { Field, Sill, Check } from '../components/ui'

/**
 * Email -> emailed code -> set a password. Length follows Supabase's
 * Email OTP Length setting (6-10), so don't hard-code six.
 *
 * The code is Supabase Auth's own email OTP rather than a hand-rolled codes
 * table: minting our own sessions would put the service-role key within reach
 * of the login path, which the brief rules out.
 *
 * Two templates matter, not one. A brand-new address gets "Confirm signup";
 * a returning one gets "Magic Link". Both must carry {{ .Token }} or the email
 * arrives as a link instead of a code — see SETUP.md.
 */

/** TODO(tom): confirm this is the address you want strangers writing to. */
const CONTACT_EMAIL = 'hello@secretplacemusic.au'

/**
 * Sign in with an account they already have.
 *
 * Enabled per provider in the Supabase dashboard — a provider that isn't
 * configured there returns "Unsupported provider" rather than doing anything
 * dangerous, so listing one early costs nothing but a clear error.
 *
 * Apple is here as well as Google because the App Store requires an Apple
 * option wherever another social sign-in is offered, and this is a PWA that is
 * meant to end up on phones.
 */
const PROVIDERS = [
  {
    id: 'google', label: 'Continue with Google',
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M23 12.27c0-.85-.08-1.67-.22-2.45H12v4.63h6.16a5.3 5.3 0 0 1-2.28 3.47v2.89h3.69C21.72 18.8 23 15.8 23 12.27z" />
        <path fill="#34A853" d="M12 23.5c3.08 0 5.67-1.02 7.57-2.77l-3.7-2.87c-1.02.69-2.33 1.1-3.87 1.1-2.98 0-5.5-2.01-6.4-4.72H1.77v2.96A11.45 11.45 0 0 0 12 23.5z" />
        <path fill="#FBBC05" d="M5.6 14.24a6.9 6.9 0 0 1 0-4.4V6.88H1.77a11.47 11.47 0 0 0 0 10.3l3.83-2.94z" />
        <path fill="#EA4335" d="M12 5.02c1.68 0 3.19.58 4.37 1.71l3.28-3.27C17.66 1.6 15.07.5 12 .5 7.55.5 3.7 3.05 1.77 6.88L5.6 9.84C6.5 7.13 9.02 5.02 12 5.02z" />
      </svg>
    ),
  },
  {
    id: 'apple', label: 'Continue with Apple',
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.05 12.54c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.61-1.7-3.18-1.73-1.35-.14-2.64.8-3.33.8-.69 0-1.75-.78-2.87-.76-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.75 2.21 1.1-.045 1.52-.71 2.85-.71 1.33 0 1.71.71 2.87.69 1.19-.02 1.94-1.08 2.66-2.14.84-1.23 1.19-2.42 1.2-2.48-.03-.01-2.3-.88-2.33-3.5zM14.88 5.2c.61-.74 1.02-1.77.91-2.8-.88.04-1.94.59-2.57 1.32-.56.66-1.05 1.71-.92 2.72.98.08 1.98-.5 2.58-1.24z" />
      </svg>
    ),
  },
]

/**
 * How long the code screen gives you, in seconds.
 *
 * This is a nudge, not the rule. Supabase decides when a token actually dies
 * (Auth -> Providers -> Email -> Email OTP Expiration, 3600s by default). Set
 * that to 600 as well or the screen and the server disagree — the screen would
 * stop accepting a code the server would still have taken.
 */
const CODE_TTL = 600
/** The shortest OTP GoTrue will issue. Not the length of ours — see the
 *  auto-submit effect for why this file must not claim to know that. */
const MIN_CODE = 6
/** How long the digits must stop changing before the code is sent by itself.
 *  Long enough to type the 7th and 8th, short enough not to feel stalled. */
const SETTLE_MS = 700

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

/**
 * Both of these live at module scope on purpose.
 *
 * Defined inside Auth(), they would be a NEW component type on every render, so
 * React would unmount and remount the input on each keystroke — and the field
 * would lose focus after every character typed. It is an easy thing to write
 * and a miserable thing to type into.
 */

/**
 * The account the password belongs to, shown rather than hidden.
 *
 * A password manager will not offer to save a credential it cannot label, and
 * the "set a password" screen has no email on it — the address was typed two
 * screens back. A `hidden` input is the usual trick, but Chrome and Safari both
 * skip username fields they consider invisible, so it is unreliable in exactly
 * the browsers that matter. Showing it read-only satisfies the manager AND
 * answers the question the screen otherwise leaves open: which account is this?
 */
function AccountField({ email }) {
  return (
    <Field label="Account">
      <input className="input" type="email" name="username" autoComplete="username"
             value={email} readOnly tabIndex={-1}
             style={{ color: 'var(--meta)', cursor: 'default' }} />
    </Field>
  )
}

function PwField({ label, value, onChange, autoComplete, placeholder, show, onToggle, autoFocus }) {
  return (
    <Field label={label}>
      <div className="pwwrap">
        <input className="input" type={show ? 'text' : 'password'} name="password"
               autoComplete={autoComplete} value={value} placeholder={placeholder}
               autoFocus={autoFocus}
               onChange={(e) => onChange(e.target.value)} />
        <button type="button" className="pwpeek" onClick={onToggle}
                aria-label={show ? 'Hide password' : 'Show password'} aria-pressed={show}>
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </Field>
  )
}

/**
 * Nothing is asked here any more.
 *
 * Signing up used to open with a name form and a four-way "what brings you
 * here?" — four screens before the account existed. Every one of those is a
 * place to leave, and none of them had to happen first: a provider hands over
 * the name, and the lane only picks a landing screen, which is a question worth
 * asking once someone is already in. Both moved to Welcome.
 *
 * The fourth option went entirely. "I've been asked to co-sign" was never a
 * kind of person — handle_new_user() already claims any co-writer row matching
 * the new address, so the song is waiting on the other side whether they say so
 * or not. Asking was making the user tell us something we knew.
 */

/** Three close-up instrument photos cross-fading behind the wordmark, replacing
 *  the old star/wave art. Decorative only — aria-hidden, pointer-events off,
 *  sits behind everything. All three stay mounted and swap opacity; unlike
 *  Backdrop.jsx's palette layers these carry no blur, so the "only render
 *  what's visible" cost that exists there to protect a phone's GPU doesn't
 *  apply here. */
const LANDING_PHOTOS = ['/landing/hero-1.jpg', '/landing/hero-2.jpg', '/landing/hero-3.jpg']
const LANDING_PHOTO_HOLD_MS = 5000

function LandingArt() {
  const still = typeof matchMedia === 'function'
    && matchMedia('(prefers-reduced-motion: reduce)').matches
  const [cur, setCur] = useState(0)

  useEffect(() => {
    if (still) return
    const id = setInterval(
      () => setCur((c) => (c + 1) % LANDING_PHOTOS.length),
      LANDING_PHOTO_HOLD_MS,
    )
    return () => clearInterval(id)
  }, [still])

  return (
    <div className="lp-art" aria-hidden="true">
      {LANDING_PHOTOS.map((src, i) => (
        <div key={src}
             className={`lp-photo${i === cur ? ' active' : ''}${still ? ' is-still' : ''}`}
             style={{ backgroundImage: `url(${src})` }} />
      ))}
      <div className="lp-scrim" />
    </div>
  )
}

/**
 * Native <dialog>, so Esc and the focus trap are the platform's job rather than
 * ours. The mailto target is a module constant — there is no user input on this
 * screen that could steer where the mail goes.
 */
function Contact({ open, onClose }) {
  const ref = useRef(null)

  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    if (!open && d.open) d.close()
  }, [open])

  /**
   * The dialog can close without React asking it to — Esc, or a form submit.
   * When that happened, `open` stayed true up here, so the next click on
   * "Contact us" set state to a value it already held, no effect re-ran, and
   * the sheet never came back. Listening to the element's own `close` event
   * keeps the two in step. Native listener rather than the React prop because
   * `close` doesn't bubble, and this is the one that must not be missed.
   */
  useEffect(() => {
    const d = ref.current
    if (!d) return
    d.addEventListener('close', onClose)
    return () => d.removeEventListener('close', onClose)
  }, [onClose])

  return (
    <dialog ref={ref} className="sheet"
            onCancel={(e) => { e.preventDefault(); onClose() }}
            onClick={(e) => { if (e.target === ref.current) onClose() }}>
      <div className="sheet-body">
        <div className="eyebrow">Contact</div>
        <h2 className="q">Talk to a person.</h2>
        <p className="sub">
          Questions about bringing your church on, or about what happens to a song
          once it's in here. A person answers these, not a queue.
        </p>
        <Sill />
        <a className="btn btn-primary" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        <button className="btn btn-quiet" style={{ marginTop: 10 }} onClick={onClose}>
          Close
        </button>
      </div>
    </dialog>
  )
}

export default function Auth() {
  const nav = useNavigate()
  const { device } = usePlatform()
  const [mode, setMode] = useState('landing')
  const [contactOpen, setContactOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [left, setLeft] = useState(0)
  const [sending, setSending] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [social, setSocial] = useState([])

  // Asked once, on arrival. Until it answers, no provider buttons render —
  // better a beat of nothing than a button that lands on an error page.
  useEffect(() => { enabledProviders().then(setSocial) }, [])

  /**
   * The providers we actually draw a button for.
   *
   * Not social.length — /auth/v1/settings lists `email: true` in the same
   * object as the OAuth providers, so the raw list is never empty and every
   * "have we got social sign-in?" test against it silently passes. That put
   * "use an account you already have" above a screen with no such button on it.
   */
  const socialOn = PROVIDERS.filter((p) => social.includes(p.id))
  const googleOn = socialOn.some((p) => p.id === 'google')
  const googleHostRef = useRef(null)
  const [, , isNight] = useTheme()

  /**
   * Google's button lives in a cross-origin iframe, so its click never
   * reaches our code as a DOM event — the only signal we get back is the
   * credential callback GIS invokes on success. That rules out an onClick
   * handler entirely; this listens instead, and re-arms after every result
   * so the button keeps working across more than one attempt.
   *
   * This used to forward Beseter's own styled click into a hidden Google
   * button instead of rendering the real one. Tested, and it failed for
   * real: `[GSI_LOGGER] Failed to open popup window` — Chrome's popup
   * blocker requires the popup to open within the exact call stack of a
   * trusted gesture, and the `await`/render work between the person's click
   * and the forwarded one was enough to lose that. Rendering Google's own
   * button means the click it sees IS the person's click.
   */
  useEffect(() => {
    if (!googleOn) return
    const host = googleHostRef.current
    if (!host) return
    let cancelled = false

    const listen = () => {
      waitForCredential()
        .then((idToken) => {
          if (cancelled) return undefined
          return run(async () => {
            const { error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: idToken })
            if (error) throw error
            // signInWithOAuth leaves via a real page navigation, which is
            // what hands control back to Gate. signInWithIdToken never
            // navigates — the session exists, but nothing has told the app
            // to look yet.
            window.location.assign(window.location.origin)
          })
        })
        .catch((e) => {
          if (cancelled) return
          // A timeout just means nobody has clicked yet — not a failure
          // worth surfacing, and the button should keep working either way.
          if (!/cancelled or timed out/.test(e.message)) setErr(readableError(e))
        })
        .finally(() => { if (!cancelled) listen() })
    }

    renderGoogleButton(host, { isNight }).then(() => { if (!cancelled) listen() })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, googleOn, isNight])

  // Ticks only while the code screen is up, and stops at zero rather than
  // running negative in a background tab.
  useEffect(() => {
    if (mode !== 'code' || left <= 0) return
    const t = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [mode, left])

  const go = (next) => { setErr(null); setMode(next) }

  /**
   * The wordmark, for someone who isn't signed in. Goes back to the start and
   * empties the run rather than just flipping the step:
   *
   * - `pw` and `code` are dropped so a half-typed password isn't left sitting in
   *   component state, and a stray Enter can't resubmit a stale one.
   * - `beseter.intent` is swept up. Nothing writes it any more — role choice
   *   moved to Welcome, after the name — but a browser that used the old flow
   *   still has the value sitting there, and Welcome no longer reads it.
   *   Clearing it here and on sign-out retires it for good.
   */
  const toLanding = () => {
    setMode('landing'); setContactOpen(false)
    setEmail(''); setCode(''); setPw('')
    setErr(null); setBusy(false); setSending(false); setLeft(0)
    localStorage.removeItem('beseter.intent')
  }

  const run = async (fn) => {
    setBusy(true); setErr(null)
    try { await fn() } catch (e) { setErr(readableError(e)) } finally { setBusy(false) }
  }

  /**
   * Move to the code screen first, send second.
   *
   * Supabase does the SMTP handshake inside the /otp request — measured at
   * 1.8-2.2s against this project — so awaiting it before navigating means two
   * seconds of a button that says "Sending…" and a screen that hasn't changed.
   * Showing the code screen immediately puts the reader in their inbox while
   * the request is still in flight, and the screen carries its own state: a
   * "sending" line that becomes "sent", or the error if it never went.
   */
  const sendCode = async () => {
    setErr(null)
    setCode(''); setSending(true); setLeft(0)
    setMode('code')

    // No metadata to send: the name and the lane are both asked on Welcome now,
    // once the account exists. handle_new_user() copes with the names being
    // absent, and Gate routes anyone missing them straight to that screen.
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: { shouldCreateUser: true },
    })

    setSending(false)
    if (error) {
      // The countdown never starts, so the screen can't offer a timer for a
      // code that was never sent.
      setErr(readableError(error))
      return
    }
    setLeft(CODE_TTL)
  }

  /**
   * A new address confirms via type 'signup'. A returning one's code is
   * generated as a recovery_token under the hood (verified directly against
   * auth.one_time_tokens — every already-confirmed user gets this type, every
   * still-unconfirmed one gets 'confirmation_token'), so it verifies via type
   * 'recovery', not 'email'. The client cannot know which case it's in
   * without asking, so try all three rather than making the writer care.
   * 'email' is tried first as the type Supabase's own docs suggest for OTP,
   * but it never actually matches either stored type here — kept as a no-cost
   * first attempt in case a future GoTrue version changes that.
   */
  /**
   * Auto-submit, once, and NOT at a hardcoded length.
   *
   * This screen assumed a six-digit code — auto-submitting on
   * `code.length === 6`, disabling the button below six, and printing a
   * six-zero placeholder. This project's GOTRUE_MAILER_OTP_LENGTH is 8, so
   * every emailed code looks like 96702732 and the screen fired off `967027`
   * the instant the sixth digit landed. The server hashed six of the eight
   * digits, found nothing, and answered "token has expired or is invalid" —
   * which is the same message it gives for a genuinely stale code, so the
   * screen blamed the code and the token sat in auth.one_time_tokens
   * unconsumed. Sign-in by email had never once worked.
   *
   * So: no length constant anywhere. Wait for the digits to stop arriving,
   * then send whatever is there. That covers 6, 8 and 10 without this file
   * needing to know which, and it covers the one-time-code autofill, which
   * drops the whole string in at once.
   *
   * A ref, not state, for the sent-guard: it has to be set synchronously in
   * the same tick, or a re-render slips a second call through before a state
   * update lands. Two in flight is not harmless — an OTP is single-use, so
   * the winner consumes the token and the loser reports failure.
   */
  const autoSent = useRef(null)
  useEffect(() => {
    if (code.length < MIN_CODE || busy) return
    if (autoSent.current === code) return
    const t = setTimeout(() => {
      autoSent.current = code
      verify()
    }, SETTLE_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, busy])

  const verify = () => run(async () => {
    const token = code.trim()
    const addr = email.trim().toLowerCase()

    // 'recovery' first: it is what the database actually stores for a
    // returning address (auth.one_time_tokens.token_type = 'recovery_token',
    // verified against live Sydney). 'email' was first and never matches, so
    // every sign-in spent a wasted round trip and logged a 403 that looked
    // like a failure to anyone reading the auth logs.
    let { error } = await supabase.auth.verifyOtp({ email: addr, token, type: 'recovery' })
    if (error) {
      const retry = await supabase.auth.verifyOtp({ email: addr, token, type: 'signup' })
      error = retry.error
    }
    if (error) {
      const retry = await supabase.auth.verifyOtp({ email: addr, token, type: 'email' })
      error = retry.error
    }
    if (error) {
      throw new Error(
        // Supabase answers a wrong code and a stale one with the same
        // "Token has expired or is invalid", so don't claim to tell them apart.
        /expired|invalid/i.test(error.message)
          ? "That code didn't match, or it has expired — send a new one."
          : "That code didn't match. Check it and try again.",
      )
    }

    /**
     * Ask the database whether a password exists, rather than a flag in user
     * metadata. The old check read user_metadata.has_password — which the
     * browser can write, so it was a claim, not a fact, and it went missing on
     * any account not created by this exact form. my_auth_methods() reads the
     * real bcrypt hash.
     */
    const { data: methods } = await supabase.rpc('my_auth_methods')
    if (methods?.[0]?.has_password) return
    go('password')
  })

  const setPassword = () => run(async () => {
    const { breached } = await isPasswordBreached(pw)
    if (breached) throw new Error('That password has appeared in a known data breach — pick a different one.')
    const { error } = await supabase.auth.updateUser({ password: pw })
    if (error) throw error
    // SessionProvider picks the session up and App routes onward.
  })

  /**
   * The session is already live by the time this screen appears — verifying the
   * code signed them in. So skipping is not "cancel", it is just leaving the
   * password unset, and Gate takes it from here.
   */
  const skipPassword = () => { setPw(''); nav('/') }

  const signIn = () => run(async () => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(), password: pw,
    })
    if (error) throw error
  })

  /**
   * Hands off to the provider and comes back to "/", where Gate re-derives the
   * landing screen from the session exactly as it does for any other sign-in.
   *
   * redirectTo is window.location.origin — the current origin, never a value
   * from the query string — so there is nothing here for an open redirect to
   * latch onto. The origin still has to be on the Supabase redirect allow-list
   * or the provider refuses the round trip.
   */
  /**
   * Apple only — Google is handled entirely by the useEffect above and
   * never calls this. Kept name-generic rather than renamed to withApple:
   * the App Store requires an Apple option wherever another social sign-in
   * is offered (see PROVIDERS above), and this is the slot the next one
   * that genuinely fits the redirect flow would use.
   */
  const withProvider = (id) => run(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: id,
      options: { redirectTo: window.location.origin },
    })
    if (error) {
      throw new Error(
        /unsupported|not enabled|provider/i.test(error.message)
          ? 'Apple sign-in isn\'t switched on for this project yet.'
          : error.message,
      )
    }
  })

  const ProviderButtons = ({ note }) => {
    if (socialOn.length === 0) return null
    return (
      <>
        <div className="oauth">
          {socialOn.map((p) => (
            p.id === 'google'
              // Google's real button renders here — see the useEffect above.
              // No onClick: the click happens inside Google's own iframe,
              // which we never receive as a DOM event.
              ? <div key="google" ref={googleHostRef} className="oauth-google-host" />
              : <button key={p.id} type="button" className="btn btn-quiet oauth-btn"
                        disabled={busy} onClick={() => withProvider(p.id)}>
                  {p.icon}<span>{p.label}</span>
                </button>
          ))}
        </div>
        <div className="orline"><span>{note || 'or use your email'}</span></div>
      </>
    )
  }

  const Back = ({ to, label }) => (
    <button className="backlink" onClick={() => go(to)}>‹ {label}</button>
  )

  const isPhone = device === 'phone'

  // The three ways in, shared verbatim between the phone landing screen and
  // the desktop portal's card — same handlers, same order, only the
  // surrounding markup differs (see below).
  const landingActions = (
    <div className={isPhone ? 'lp-minimal-actions' : 'portal-actions'}>
      <button className="btn btn-glass" onClick={() => go('email')}>Sign up</button>
      <button className="btn btn-glass-outline" onClick={() => go('signin')}>Sign in</button>
      <button className={isPhone ? 'lp-contact-btn-minimal' : 'btn-plain'}
              onClick={() => setContactOpen(true)}>
        Contact us
      </button>
    </div>
  )

  // Every mode below this line is untouched by the portal work — same JSX,
  // same handlers, on a phone or a desktop. Only the landing screen and the
  // chrome wrapping it (Shell vs. the portal frame) branch on device; a form
  // is a form regardless of how much room it's sitting in.
  const steps = (
    <>
      {/* Hoisted above the per-mode branches: on a phone "Contact us" only
          ever appears on the landing screen, but the portal's pill row keeps
          it visible on every step, so the dialog it opens has to be mounted
          regardless of which mode is showing rather than nested inside one. */}
      <Contact open={contactOpen} onClose={() => setContactOpen(false)} />

      {mode === 'landing' && (isPhone ? (<div className="lp lp-minimal">
        <LandingArt />

        <div className="lp-center">
          <h1 className="lp-title">BESETER</h1>
          {landingActions}
        </div>
      </div>) : (<div className="portal-landing">
        <div className="eyebrow">Get started</div>
        <h2 className="q">Create an account, or sign back in.</h2>
        <p className="sub">Pick up where you left off, or start something new.</p>
        <Sill />
        {landingActions}
      </div>))}

      {mode === 'email' && (<>
        <Back to="landing" label="Back" />
        <div className="eyebrow">New here</div>
        {/* The copy follows the buttons. With no providers switched on there is
            no "account you already have" to offer, and promising one is worse
            than not mentioning it. */}
        <h2 className="q">{socialOn.length ? 'How do you want to sign in?' : "What's your email?"}</h2>
        <p className="sub">
          {socialOn.length
            ? "Use an account you already have, or give us an email and we'll send a code."
            : "We'll send you a code. Nothing to remember, and no password unless you want one."}
        </p>
        <Sill />

        <ProviderButtons />

        <form onSubmit={(e) => { e.preventDefault(); if (email.includes('@')) sendCode() }}>
          <Field label="Email">
            <input className="input" type="email" name="email" autoComplete="email"
                   value={email} placeholder="you@church.org" autoFocus
                   onChange={(e) => setEmail(e.target.value)} />
          </Field>
          {/* No busy state: this moves to the code screen on submit, and the
              send reports itself over there. */}
          <button type="submit" className="btn btn-primary" disabled={!email.includes('@')}>
            Send me a code
          </button>
        </form>
        <button className="btn-plain" style={{ width: '100%', marginTop: 8 }}
                onClick={() => go('signin')}>I already have a password</button>
      </>)}

      {mode === 'code' && (<>
        <Back to="email" label="Wrong email? Change it" />
        <div className="eyebrow">Check your email</div>
        <h2 className="q">Type the code.</h2>
        <p className="sub" aria-live="polite">
          {sending
            ? <><span className="dot-pulse" aria-hidden="true" />Sending to {email}…</>
            : err
              ? <>Nothing went to {email}.</>
              : <>
                  Sent to {email}.{' '}
                  {left > 0
                    ? <>Good for about <span className="countdown">{mmss(left)}</span>.</>
                    : <>That one may have run out — try it, or send a new one.</>}
                  {' '}Use the newest email — asking for another code stops the
                  earlier one working.
                </>}
        </p>
        <Sill />
        <Field label="Code">
          {/* autoComplete="one-time-code" is what lets iOS and macOS offer the
              code straight from Mail, without opening it. Keep it. */}
          <input className="input otp" inputMode="numeric" autoComplete="one-time-code"
                 maxLength={10} value={code} autoFocus placeholder="Your code"
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                 onKeyDown={(e) => { if (e.key === 'Enter' && code.length >= MIN_CODE) verify() }} />
        </Field>
        {/* Not disabled on `left === 0`. CODE_TTL is this file's guess at the
            server's expiry, not the server's answer — when the guess ran short
            the screen refused to send a code that was still perfectly good,
            and the only way out was requesting another one (which invalidates
            the one in the inbox). Let the server rule on it. */}
        <button className={`btn btn-primary${busy ? ' loading' : ''}`}
                disabled={busy || code.length < MIN_CODE} onClick={verify}>
          {busy && <span className="btn-spin" aria-hidden="true" />}
          {/* Was keyed on `code.length === 6`, so it read "Verifying…" the
              moment a sixth character existed — idle, not verifying — and
              flipped back to "Continue" at the eighth. It reflects the request
              now, which is the only thing it was ever meant to describe. */}
          <span className="btn-label">{busy ? 'Verifying…' : 'Continue'}</span>
        </button>
        <button className="btn-plain" style={{ width: '100%', marginTop: 8 }}
                disabled={sending} onClick={sendCode}>
          {sending ? 'Sending…' : 'Send it again'}
        </button>
        {/* This said "the email templates need fixing", which told the person
            signing in about a defect they can do nothing about. Kept only as a
            way through — if the template is emitting a link rather than
            {'{{ .Token }}'}, the six digits are still in the URL after
            token=. Delete this block once the Magic Link and Reset Password
            templates are confirmed to carry the code. */}
        <p className="small" style={{ marginTop: 12 }}>
          Got a link instead of a code? Open the link — it signs you in directly.
        </p>
      </>)}

      {mode === 'password' && (<>
        <div className="eyebrow">You're in</div>
        <h2 className="q">Want a password?</h2>
        <p className="sub">
          You don't need one — we can email you a code every time. A password just
          makes getting back in quicker, and your password manager can hold it.
        </p>
        <Sill />
        {/* A real form, with the address in it. Both are what a password manager
            needs before it will offer to save anything. */}
        <form onSubmit={(e) => { e.preventDefault(); if (pw.length >= 8) setPassword() }}>
          <AccountField email={email} />
          <PwField label="New password" value={pw} onChange={setPw} autoFocus
                   autoComplete="new-password" placeholder="At least 8 characters"
                   show={showPw} onToggle={() => setShowPw(!showPw)} />
          <button type="submit" className={`btn btn-primary${busy ? ' loading' : ''}`}
                  disabled={busy || pw.length < 8}>
            {busy && <span className="btn-spin" aria-hidden="true" />}
            <span className="btn-label"><Check /> Save it and continue</span>
          </button>
        </form>
        <button className="btn-plain" style={{ width: '100%', marginTop: 8 }}
                onClick={skipPassword}>
          Skip — email me a code each time
        </button>
      </>)}

      {mode === 'signin' && (<>
        <Back to="landing" label="Back" />
        <div className="eyebrow">Welcome back</div>
        <h2 className="q">Sign in.</h2>
        <Sill />

        <ProviderButtons note="or with your email" />

        <form onSubmit={(e) => { e.preventDefault(); if (pw) signIn() }}>
          <Field label="Email">
            <input className="input" type="email" name="email" autoComplete="email"
                   value={email} autoFocus onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <PwField label="Password" value={pw} onChange={setPw}
                   autoComplete="current-password"
                   show={showPw} onToggle={() => setShowPw(!showPw)} />
          <button type="submit" className="btn btn-primary" disabled={busy || !pw}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <button className="btn-plain" style={{ width: '100%', marginTop: 8 }}
                onClick={() => go('email')}>Email me a code instead</button>
        <button className="btn-plain" style={{ width: '100%' }}
                onClick={() => go('landing')}>I'm new here</button>
      </>)}

      {err && <p className="err">{err}</p>}
    </>
  )

  // Desktop and tablet get a dashboard frame — a rail, a pill row that
  // doubles as real navigation between sign-up and sign-in (not decoration:
  // each pill is the same go() call the phone version's buttons make), and
  // the exact step content above sitting in a card. --portal-wash (styles.css)
  // is the one thing meant to be swapped for a background image later; the
  // rest of this doesn't change when that happens.
  if (!isPhone) {
    const signingUp = mode === 'email' || mode === 'code' || mode === 'password'
    return (
      <div className="portal-auth">
        <div className="portal-main">
          {/* One wordmark, doing one job: home. It used to sit alone in a
              rail down the left edge, disconnected from the page it names —
              now it introduces the hero it sits above, like every other
              signed-in screen's topbar does. */}
          <button type="button" className="portal-mark" onClick={toLanding}
                  aria-label="Beseter — home">
            <span className="portal-mark-glyph" aria-hidden="true">B</span>
            beseter
          </button>
          {/* The landing card already offers Sign up / Sign in / Contact us as
              its own buttons — showing the pill row on top of it duplicated
              every action. The pills only earn their place once the visitor
              has committed to a mode, as a way to switch legs mid-flow. */}
          {mode !== 'landing' && (
            <div className="portal-pillrow">
              <button className={`portal-pill${signingUp ? ' on' : ''}`} onClick={() => go('email')}>
                Sign up
              </button>
              <button className={`portal-pill${mode === 'signin' ? ' on' : ''}`} onClick={() => go('signin')}>
                Sign in
              </button>
              <button className="portal-pill portal-pill-ghost" onClick={() => setContactOpen(true)}>
                Contact us
              </button>
            </div>
          )}
          <div className="portal-hero">
            <h1>Welcome to Beseter<span>Songwriter to church, one submission at a time.</span></h1>
          </div>
          <div className="portal-card">{steps}</div>
        </div>
      </div>
    )
  }

  return (
    <Shell onHome={toLanding}>
      {steps}
    </Shell>
  )
}
